export interface AddressParts {
  street?: string | null;
  streetNumber?: string | null;
  district?: string | null;
  city?: string | null;
  subregion?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
  isoCountryCode?: string | null;
}

export const isGpsAddressField = (field: { tipo: string; endereco_gps?: unknown }): boolean =>
  field.tipo === 'text' && (field.endereco_gps === true || field.endereco_gps === 1);

const clean = (value?: string | null): string => value?.trim().replace(/\s+/g, ' ') ?? '';
const normalize = (value: string): string => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const BRAZIL_STATES: Record<string, string> = {
  acre: 'AC', alagoas: 'AL', amapa: 'AP', amazonas: 'AM', bahia: 'BA', ceara: 'CE',
  'distrito federal': 'DF', 'espirito santo': 'ES', goias: 'GO', maranhao: 'MA',
  'mato grosso': 'MT', 'mato grosso do sul': 'MS', 'minas gerais': 'MG', para: 'PA',
  paraiba: 'PB', parana: 'PR', pernambuco: 'PE', piaui: 'PI', 'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN', 'rio grande do sul': 'RS', rondonia: 'RO', roraima: 'RR',
  'santa catarina': 'SC', 'sao paulo': 'SP', sergipe: 'SE', tocantins: 'TO',
};

/** Usa apenas componentes informados pelo geocoder; nunca deduz número ou complemento. */
export const formatGpsAddress = (address: AddressParts): string => {
  const street = clean(address.street);
  const number = street ? clean(address.streetNumber) : '';
  const city = clean(address.city) || clean(address.subregion);
  const district = clean(address.district);
  const countryCode = clean(address.isoCountryCode).toUpperCase();
  const countryName = clean(address.country);
  const isBrazil = countryCode === 'BR' || ['brasil', 'brazil'].includes(normalize(countryName));
  const country = isBrazil ? 'Brasil' : countryName || countryCode;
  const originalRegion = clean(address.region);
  const region = isBrazil ? BRAZIL_STATES[normalize(originalRegion)]
    || (Object.values(BRAZIL_STATES).includes(originalRegion.toUpperCase()) ? originalRegion.toUpperCase() : originalRegion)
    : originalRegion;
  const originalPostalCode = clean(address.postalCode);
  const postalCode = isBrazil && /^\d{8}$/.test(originalPostalCode)
    ? `${originalPostalCode.slice(0, 5)}-${originalPostalCode.slice(5)}` : originalPostalCode;
  if (![street, district, city, postalCode].some(Boolean)) return '';
  const parts = [
    [street, number].filter(Boolean).join(', '),
    district,
    [city, region].filter(Boolean).join(' / '),
    postalCode,
    country,
  ].filter(Boolean);
  return [...new Set(parts)].join(' - ');
};

export class GpsAddressError extends Error {}

export const GPS_ADDRESS_MESSAGES = {
  denied: 'Localização não autorizada. Digite o endereço ou permita o acesso nos ajustes.',
  unavailable: 'Não foi possível obter o endereço. Confira a conexão e tente novamente ou digite manualmente.',
  timeout: 'A localização demorou demais. Tente novamente ou digite o endereço.',
  empty: 'Nenhum endereço encontrado. Digite o endereço manualmente.',
  web: 'O preenchimento por GPS está disponível no aplicativo Android ou iOS. Aqui, digite o endereço.',
  cancelled: 'Consulta cancelada.',
};
